import React, { useState } from 'react';
import {
  makeStyles,
  withStyles,
  ThemeProvider,
} from '@mui/styles';
import { createTheme } from '@mui/material/styles';

import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Checkbox from '@mui/material/Checkbox';
import KeyHandler, { KEYPRESS } from 'react-key-handler';
import Moment from 'moment';
import { COLORS } from 'src/constants/colors';

const theme = createTheme({
  overrides: {
    MuiTableRow: {
      root: {
        '&$selected, &$selected:hover': {
          backgroundColor: '#bedcfa',
        },
      },
      row: {
        // for the body
        height: 10,
      },
      head: {
        // for the head
        height: 10,
      },
    },
  },
});

const columns = [{ id: 'name', label: 'Name', minWidth: 80, type: 'label' }];

const useStyles = makeStyles({
  root: {
    width: '100%',
  },
  container: {
    maxHeight: 500,
  },
  row: {
    border: 'none',
    height: 10,
  },
});
const CustomCheckbox = withStyles({
  root: {
    color: COLORS.cta_blue,
    '&$checked': {
      color: COLORS.cta_blue,
    },
  },
  checked: {},
})((props) => <Checkbox color="default" {...props} />);

export default function SearchTable({
  rows,
  rowSelected,
  multiSelection = false,
  checkedRows = [],
  setCheckedRows = () => { },
}) {
  const classes = useStyles();
  const [selected, setSelected] = useState(rows.length > 0 ? rows[0].id : '');
  const [rowData, setRows] = useState(rows);
  const [currentSelected, setCurrent] = useState(rows.length > 0 ? 0 : -1);

  function handleClick(row) {
    if (multiSelection) {
      handleRowCheck(row?.id);
    } else {
      const data = [findArrayElement(rows, row.id)];
      rowSelected(data);
    }
  }

  function findArrayElement(array, id) {
    return array.find((obj) => {
      return obj.id === id;
    });
  }
  const isRowChecked = (id) => {
    return (checkedRows ?? []).findIndex((r) => r?.id == id) !== -1;
  };

  function keyUp() {
    if (currentSelected == -1) {
      setCurrent(0);
      setSelected(rows[0].id);
      setRows([...rows]);
    } else if (currentSelected > -1) {
      if (currentSelected == 0) {
        var val = rows.length - 1;
        setCurrent(val);
        setSelected(rows[val].id);
        setRows([...rows]);
      } else {
        var val = currentSelected - 1;
        setCurrent(val);
        setSelected(rows[val].id);
        setRows([...rows]);
      }
    }
  }

  function keyDown() {
    if (currentSelected < rows.length - 1) {
      const val = currentSelected + 1;

      setCurrent(val);
      setSelected(rows[val].id);
      setRows([...rows]);
    } else if (currentSelected == rows.length - 1) {
      setCurrent(0);
      setSelected(rows[0].id);
      setRows([...rows]);
    }
  }

  function rowClicked() {
    if (currentSelected == -1) {
    } else {
      const data = [findArrayElement(rows, rows[currentSelected].id)];
      rowSelected(data);
    }
  }

  const handleRowCheck = (id) => {
    const selectedIndex = checkedRows
      ?.map((company) => company?.id)
      .indexOf(id);
    const data = [findArrayElement(rows, id)];

    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(checkedRows, data);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(checkedRows.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(checkedRows.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        checkedRows.slice(0, selectedIndex),
        checkedRows.slice(selectedIndex + 1),
      );
    }
    setCheckedRows(newSelected);
  };

  const handleAllRowCheck = (event) => {
    let previousRows = [...(checkedRows ?? [])];
    if (event.target.checked == true) {
      rows?.forEach((row) => {
        const selectedIndex = previousRows.findIndex((r) => r?.id == row?.id);
        if (selectedIndex === -1) {
          previousRows.push({
            id: row?.id,
            value: row?.name,
            label: row?.name,
          });
        }
      });
      setCheckedRows(previousRows);
    } else if (event.target.checked == false) {
      setCheckedRows([]);
    }
  };

  function addNew() { }

  return (
    <Paper className={classes.root}>
      <KeyHandler keyValue="ArrowUp" onKeyHandle={keyUp} />
      <KeyHandler keyValue="ArrowDown" onKeyHandle={keyDown} />
      <KeyHandler
        keyEventName={KEYPRESS}
        keyValue="Enter"
        onKeyHandle={() => {
          multiSelection ? handleRowCheck(selected) : rowClicked();
        }}
      />
      <KeyHandler keyValue="F2" onKeyHandle={addNew} />
      <TableContainer className={classes.container}>
        <Table stickyHeader size="small" aria-label="sticky table">
          <TableHead>
            <TableRow>
              {multiSelection ? (
                <TableCell key="multiselect" align="center" padding="checkbox">
                  <CustomCheckbox
                    checked={
                      rows?.length === checkedRows?.length && rows?.length > 0
                        ? true
                        : false
                    }
                    onChange={(event) => handleAllRowCheck(event)}
                  />
                </TableCell>
              ) : (
                <></>
              )}
              {columns.map((column) => (
                <TableCell
                  key={column.id}
                  align={column.align}
                  style={{
                    minWidth: column.minWidth,
                    background: '#000000',
                    color: '#ffffff',
                    height: '10px',
                  }}
                >
                  {column.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => {
              return (
                <ThemeProvider theme={theme}>
                  <TableRow
                    hover
                    className={classes.row}
                    role="checkbox"
                    tabIndex={-1}
                    key={row.id}
                    selected={row.id === selected}
                    onClick={(ev) => handleClick(row)}
                    classes={{
                      hover: classes.hover,
                      selected: classes.selected,
                    }}
                  >
                    {multiSelection ? (
                      <TableCell
                        key="multiselect"
                        align="center"
                        padding="checkbox"
                      >
                        <CustomCheckbox
                          checked={isRowChecked(row.id)}
                          onChange={(event) => handleRowCheck(row?.id)}
                        />
                      </TableCell>
                    ) : (
                      <></>
                    )}
                    {columns.map((column) => {
                      const value = row[column.id];
                      if (column.type == 'date') {
                        const date =
                          value === ''
                            ? ''
                            : Moment(value).format('DD-MM-YYYY');
                        return (
                          <TableCell key={column.id} align={column.align}>
                            {date}
                          </TableCell>
                        );
                      }
                      return (
                        <TableCell key={column.id} align={column.align}>
                          {column.format && typeof value === 'number'
                            ? column.format(value)
                            : value}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                </ThemeProvider>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}
